import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import Footer from 'flarum/forum/components/Footer';
import Link from 'flarum/common/components/Link';
import Navigation from 'flarum/common/components/Navigation';
import { forumHome } from './utils/portal';
import SectionsNav from './components/SectionsNav';
import SiteFooter from './components/SiteFooter';
import { portalUrl } from './utils/portal';
import { logoMark } from './icons';
import SessionDropdown from 'flarum/forum/components/SessionDropdown';
import LinkButton from 'flarum/common/components/LinkButton';
import Avatar from 'flarum/common/components/Avatar';
import username from 'flarum/common/helpers/username';
/** Unread notifications of the current user (the number core's notifications control uses). */
const unreadNotifications = () => app.session.user?.unreadNotificationCount() || 0;
const count = n => n > 0 ? m("span", {
  className: "TpCount"
}, n) : null;
export default function addHeaderAndFooter() {
  // The header title is server-rendered (no Mithril component), so the brand is set once after mount.
  // ForumApplication is not exported to the module registry; the app instance's prototype is the same object.
  extend(Object.getPrototypeOf(app), 'mount', function () {
    const home = document.getElementById('home-link');
    if (home && !home.querySelector('.TpBrand-mark')) {
      home.classList.add('TpBrand');
      home.setAttribute('aria-label', app.forum.attribute('title'));
      home.innerHTML = `<svg class="TpBrand-mark" viewBox="0 0 28 28" fill="none" aria-hidden="true">${logoMark}</svg>` + `<span class="TpBrand-name">TarotPortal</span>` + `<span class="TpBrand-label">${app.translator.trans('tarotportal-theme.forum.brand.forum_label', {}, true)}</span>`;
    }

    // The sections row is a sibling of the first-row container, not a child: core's
    // OverflowingList measures free space as "container minus the other children", so a
    // full-width child in the same container would leave it no room to measure.
    const header = document.getElementById('header');
    if (header && !document.getElementById('tp-sections')) {
      const el = document.createElement('div');
      el.id = 'tp-sections';
      el.className = 'Header-sections';
      header.appendChild(el);
      m.mount(el, SectionsNav);
    }
  });

  // The forum links only to itself; the way back to the main portal is one explicit
  // external action next to search and the account controls.
  extend(HeaderSecondary.prototype, 'items', function (items) {
    items.add('tp-portal', m("a", {
      className: "TpPill",
      href: portalUrl('/')
    }, app.translator.trans('tarotportal-theme.forum.nav.portal'), " \u2197"), 40);
  });

  // Phone bar (the drawer-enabled Navigation): brand on the left, the menu button moves to the right via CSS.
  extend(Navigation.prototype, 'items', function (items) {
    if (!this.attrs.drawer) return;
    items.add('tp-brand', m(Link, {
      className: "TpBrand TpBrand--bar",
      href: forumHome()
    }, m("svg", {
      className: "TpBrand-mark",
      viewBox: "0 0 28 28",
      fill: "none",
      "aria-hidden": "true"
    }, m.trust(logoMark)), m("span", {
      className: "TpBrand-name"
    }, "TarotPortal"), m("span", {
      className: "TpBrand-label"
    }, app.translator.trans('tarotportal-theme.forum.brand.forum_label'))), 85);

    // Signed-in users: the account control next to the menu button — core's SessionDropdown,
    // the same component (and menu) as in the open menu, placed at the same spot.
    if (app.session.user) {
      items.add('tp-account', m("div", {
        className: "TpBarAccount"
      }, m(SessionDropdown, null)), 84);
    }
  });

  // One account menu. On phones the open menu shows only the avatar (next to ✕); notifications
  // and reports move from their own header controls into this menu. The items below are part of
  // core's SessionDropdown — the same component as on desktop, where CSS keeps them hidden
  // because the header shows notifications and reports separately. Everything is gated the way
  // core gates it: reports by the canViewFlags permission, admin panel by adminUrl (core item).
  extend(SessionDropdown.prototype, 'items', function (items) {
    const user = app.session.user;
    if (!user) return;
    const group = (user.groups() || []).find(g => g && !g.isHidden());
    items.add('tp-user', m(Link, {
      className: "TpUserMenu-head",
      href: app.route.user(user)
    }, m(Avatar, {
      user: user
    }), m("span", {
      className: "TpUserMenu-who"
    }, m("span", {
      className: "TpUserMenu-name"
    }, username(user)), group ? m("span", {
      className: "TpUserMenu-role"
    }, group.nameSingular()) : null)), 200);
    items.add('tp-notifications', m(LinkButton, {
      icon: "fas fa-bell",
      href: app.route('notifications')
    }, app.translator.trans('core.forum.notifications.title'), count(unreadNotifications())), 90);
    if (app.forum.attribute('canViewFlags') && app.routes.flags) {
      items.add('tp-flags', m(LinkButton, {
        icon: "fas fa-flag",
        href: app.route('flags')
      }, app.translator.trans('flarum-flags.forum.flagged_posts.title'), count(app.forum.attribute('flagCount') || 0)), 80);
    }
  });

  // "Админ-панель" rather than the language pack's "Управление" (core's item and permission check)
  extend(SessionDropdown.prototype, 'items', function (items) {
    if (!items.has('administration')) return;
    items.setContent('administration', m(LinkButton, {
      icon: "fas fa-wrench",
      href: app.forum.attribute('adminUrl'),
      target: "_blank"
    }, app.translator.trans('tarotportal-theme.forum.nav.admin_panel')));
  });

  // Unread notifications as a small mark on the avatar (shown on phones only)
  extend(SessionDropdown.prototype, 'getButtonContent', function (content) {
    const n = unreadNotifications();
    if (n > 0) content.push(m("span", {
      className: "TpUserBadge",
      "aria-label": String(n)
    }, n > 9 ? '9+' : n));
  });
  override(Footer.prototype, 'view', () => m(SiteFooter, null));
}